import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  Box,
  TextField,
  Button,
  FormControl,
  CircularProgress,
  Backdrop,
  Typography,
} from "@mui/material";
import { isEmail } from "class-validator";
import axios from "axios";

import SignUpImg from "../../assets/auth/signup.jpg";
import Logo from "../../assets/logo/biglogo.svg";
import LogoDark from "../../assets/logo/biglogo_dark.svg";
// import { useAuthContext } from "../../hooks/contexts/useAuthContext";
import GuestGuard from "../../guards/GuestGuard";
import { useSnackbar } from "notistack";

const SignUpPage = () => {
  const navigate = useNavigate();
  const darkMode = localStorage.getItem("darkMode") === "true";
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false); // Loading state

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm({
    defaultValues: {
      username: "",
      password: "",
      contactname: "",
      practicename: "",
      address1: "",
      address2: "",
      city: "",
      state: "",
      country: "",
      postalcode: "",
      contactnumber: "",
      email: "",
    },
  });

  const onSubmit = async (data) => {
    setLoading(true);
    const newData = {
      ...data,
      contactname: data.username,
      phasedefaults: "4,2,3,",
      features: "",
      customerid: "",
      blurb: "",
    };
    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/auth/user/signup`,
        newData
      );
      enqueueSnackbar(
        <>
          Signup Successful! <br />
          Please wait, Admin approves your registration!
        </>,
        { variant: "success", autoHideDuration: 4000 }
      );
      setLoading(false);
      navigate("/login"); // Redirect to login page after successful signup
      // Optional: reset form, redirect, etc.
    } catch (err) {
      if (err.response) {
        const status = err.response.status;
        const msg = err.response.data?.message || "An error occurred.";
        if (status === 409) {
          enqueueSnackbar("User already registered.", { variant: "error" });
        } else {
          enqueueSnackbar(msg, { variant: "error" });
        }
      } else if (err.request) {
        enqueueSnackbar("No response from server.", { variant: "error" });
      } else {
        enqueueSnackbar("Request failed.", { variant: "error" });
      }
    }
  };

  return (
    <GuestGuard>
      <>
        <Backdrop sx={{ zIndex: 1400 }} open={loading}>
          <CircularProgress size={80} />
        </Backdrop>
        <Box
          sx={{
            display: "flex",
            height: "100vh",
          }}
        >
          <Box
            sx={{
              flex: { xs: 6, sm: 6, md: 5, lg: 6 },
              display: { xs: "none", sm: "none", md: "flex" },
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
            }}
          >
            <img
              src={SignUpImg}
              alt="SignUp Background"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
            />
            {/* Welcome Message Overlay */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: "linear-gradient(135deg, rgba(33, 150, 243, 0.7) 0%, rgba(21, 101, 192, 0.7) 100%)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                color: "#FFFFFF",
                textAlign: "center",
                padding: "40px",
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 700,
                  fontSize: { md: "3rem", lg: "3.5rem" },
                  marginBottom: "20px",
                  textShadow: "2px 2px 4px rgba(0,0,0,0.3)",
                }}
              >
                Welcome to 3D Practice!
              </Typography>
              <Typography
                variant="h5"
                sx={{
                  fontFamily: "Poppins, sans-serif",
                  fontWeight: 500,
                  fontSize: { md: "1.5rem", lg: "1.8rem" },
                  lineHeight: 1.4,
                  maxWidth: "600px",
                  textShadow: "1px 1px 2px rgba(0,0,0,0.3)",
                }}
              >
                Please Complete the Form to Begin your{" "}
                <Box component="span" sx={{ fontWeight: 700, color: "#FFD700" }}>
                  14 Day Trial
                </Box>
              </Typography>
              <Box
                sx={{
                  width: "60px",
                  height: "4px",
                  background: "#FFD700",
                  marginTop: "30px",
                  borderRadius: "2px",
                }}
              />
            </Box>
          </Box>
          <Box
            sx={{
              flex: { xs: 4, sm: 4, md: 5, lg: 4 },
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: darkMode ? "#1D1E21" : "#FFFFFF",
              color: darkMode ? "#fff" : "#000",
              px: 1,
            }}
          >
            <Box
              sx={{
                width: "100%",
                textAlign: "center",
                p: 2,
                overflowY: "auto",
                maxHeight: "100vh",
              }}
            >
              <form onSubmit={handleSubmit(onSubmit)}>
                <Box sx={{ mt: 2, mb: "50px" }}>
                  <img
                    src={darkMode ? LogoDark : Logo}
                    alt="3D Practice Logo"
                    style={{ width: "90%", maxWidth: 480 }}
                  />
                </Box>
                <Box
                  display="grid"
                  textAlign="left"
                  sx={{
                    gridTemplateColumns: {
                      xs: "repeat(1, 1fr)",
                    },
                  }}
                  columnGap={2}
                >
                  <FormControl fullWidth margin="normal">
                    <TextField
                      label="Practice Name"
                      placeholder="Enter Practice Name"
                      {...register("practicename", {
                        required: "Practice Name is required",
                      })}
                      error={!!errors.practicename}
                      helperText={errors.practicename?.message}
                    />
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <TextField
                      label="Username"
                      placeholder="Enter Username"
                      {...register("username", {
                        required: "Username is required",
                      })}
                      error={!!errors.username}
                      helperText={errors.username?.message}
                    />
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <TextField
                      label="Password"
                      type="password"
                      placeholder="Enter Password"
                      {...register("password", {
                        required: "Practice Password is required",
                      })}
                      error={!!errors.password}
                      helperText={errors.password?.message}
                    />
                  </FormControl>

                  <FormControl fullWidth margin="normal">
                    <TextField
                      label="Email"
                      placeholder="Enter Email"
                      {...register("email", {
                        required: "Email is required",
                        validate: (value) =>
                          isEmail(value) ||
                          "Please enter a valid email address",
                      })}
                      error={!!errors.email}
                      helperText={errors.email?.message}
                    />
                  </FormControl>
                </Box>

                <Box
                  display="flex"
                  flexDirection="column"
                  alignItems="center"
                  sx={{
                    mt: 3,
                  }}
                >
                  <Button
                    type="submit"
                    variant="contained"
                    sx={{
                      width: "50%",
                      borderRadius: "5px",
                      fontFamily: "Poppins",
                      fontSize: "18px",
                      fontWeight: 500,
                      px: 6,
                      py: 1,
                      textAlign: "center",
                    }}
                  >
                    Start Trial
                  </Button>
                  <Box
                    mt={2}
                    sx={{
                      fontSize: "16px",
                      color: darkMode ? "#BEBEBE" : "#636363",
                    }}
                  >
                    Already have an account?{" "}
                    <span
                      style={{
                        color: "#2196F3",
                        cursor: "pointer",
                      }}
                      onClick={() => navigate("/login")}
                    >
                      Log in
                    </span>
                  </Box>
                </Box>
              </form>
            </Box>
          </Box>
        </Box>
      </>
    </GuestGuard>
  );
};

export default SignUpPage;